import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Scissors, Mail, AlertTriangle, Check } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      console.error("Forgot password request failed:", err);
      if (!err.response) {
        setError("Network issue. Check your connection and try again.");
      } else {
        // Backend always returns 200 to avoid leaking emails, so this should be rare.
        setError("Couldn't send a reset link. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 animate-fade-in">
      <div className="w-full max-w-md cm-card p-8 md:p-10">
        <div className="flex items-center gap-2 mb-2">
          <Scissors className="w-5 h-5 text-copper" />
          <span className="cm-heading text-xl">RESET PASSWORD</span>
        </div>
        <p className="text-sm text-gray-400 mb-8">
          Enter the email on your account. We&rsquo;ll send a reset link.
        </p>

        {sent ? (
          <div
            className="cm-card p-6 border-copper/50 space-y-3"
            data-testid="forgot-success-banner"
          >
            <div className="flex items-start gap-2 text-copper">
              <Check className="w-5 h-5 mt-0.5" />
              <p className="text-sm leading-relaxed">
                If an account exists for <b>{email}</b>, a reset link is on the way. It expires
                in 60 minutes. Check your inbox (and spam folder).
              </p>
            </div>
            <Link to="/login" className="cm-btn-secondary w-full inline-flex items-center justify-center" data-testid="forgot-back-to-login">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="forgot-email" className="cm-label block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="cm-input pl-10"
                  placeholder="you@domain.com"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            {error && (
              <div role="alert" aria-live="assertive" className="flex items-start gap-2 text-sm text-red-400" data-testid="forgot-error">
                <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden="true" /> {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="cm-btn-primary w-full"
              data-testid="forgot-submit-button"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-gray-400 text-center">
          Remembered it?{" "}
          <Link to="/login" className="text-copper hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
